import { useState } from "react";
import { Link } from "react-router-dom";
import { Tray, Gear, CheckCircle, CalendarBlank, FileX, Warning } from "@phosphor-icons/react";

const PRIO = {
  high: "bg-rose-50 text-rose-700 border-rose-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-zinc-100 text-zinc-500 border-zinc-200",
};
const COLS = [
  { key: "received", label: "Returns received", icon: Tray, hint: "Intake & docs gathering" },
  { key: "processing", label: "In processing", icon: Gear, hint: "Prep, review & signature" },
  { key: "completed", label: "Completed", icon: CheckCircle, hint: "E-filed / accepted" },
];

function Column({ col, items }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? items : items.slice(0, 7);
  const Icon = col.icon;
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={`returns-col-${col.key}`}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-medium text-zinc-900 flex items-center gap-1.5"><Icon size={14} /> {col.label}</p>
        <span className="font-mono text-sm text-zinc-900" data-testid={`returns-count-${col.key}`}>{items.length}</span>
      </div>
      <p className="text-[10px] text-zinc-400 mb-3">{col.hint} · ranked by priority</p>
      <div className="space-y-1.5">
        {visible.map((r) => (
          <Link key={r.id} to={`/clients/${r.client_id}`} data-testid={`return-item-${r.id}`}
            className="block rounded-md border border-zinc-100 px-2.5 py-2 hover:border-zinc-300 transition-colors duration-150">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-zinc-800 truncate">{r.client_name}</span>
              <span className={`text-[9px] uppercase border rounded-sm px-1.5 py-0.5 shrink-0 ${PRIO[r.priority]}`}>{r.priority}</span>
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-[10px] text-zinc-400">
              <span>{r.service_type} · {r.tax_year}</span>
              {r.days_left != null && r.stage !== "accepted" && r.stage !== "archived" && (
                <span className={`flex items-center gap-0.5 ${r.days_left <= 30 ? "text-rose-600" : ""}`}><CalendarBlank size={10} /> {r.days_left}d</span>
              )}
              {r.missing_docs > 0 && <span className="flex items-center gap-0.5 text-amber-600"><FileX size={10} /> {r.missing_docs} missing</span>}
              {r.escalated && <span data-testid={`escalation-flag-${r.id}`} className="flex items-center gap-0.5 text-rose-600 font-medium"><Warning size={10} weight="fill" /> {r.escalated_gaps} stale gap{r.escalated_gaps > 1 ? "s" : ""}</span>}
            </div>
          </Link>
        ))}
        {items.length === 0 && <p className="text-xs text-zinc-400 italic">Nothing here.</p>}
        {items.length > 7 && (
          <button data-testid={`returns-more-${col.key}`} onClick={() => setShowAll(!showAll)} className="text-[10px] text-zinc-500 hover:text-zinc-900 transition-colors duration-150">
            {showAll ? "Show less" : `+${items.length - 7} more`}
          </button>
        )}
      </div>
    </div>
  );
}

export function ReturnsBoard({ board }) {
  const [preparer, setPreparer] = useState("all");
  const [form, setForm] = useState("all");
  const [win, setWin] = useState("all");
  if (!board) return null;
  const all = [...(board.received || []), ...(board.processing || []), ...(board.completed || [])];
  const preparers = [...new Set(all.map((r) => r.assigned_to || "Unassigned"))].sort();
  const forms = [...new Set(all.map((r) => r.service_type))].sort();
  const inWindow = (r) => {
    if (win === "all") return true;
    if (r.days_left == null) return false;
    if (win === "overdue") return r.days_left < 0;
    return r.days_left >= 0 && r.days_left <= Number(win);
  };
  const filt = (items) => (items || []).filter((r) =>
    (preparer === "all" || (r.assigned_to || "Unassigned") === preparer) &&
    (form === "all" || r.service_type === form) && inWindow(r));
  const sel = "text-xs border border-zinc-200 rounded-md px-2 py-1.5 bg-white text-zinc-700";
  return (
    <div data-testid="returns-board">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide">Tax return production — received → processed → completed</p>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="filter-preparer" className={sel} value={preparer} onChange={(e) => setPreparer(e.target.value)}>
            <option value="all">All preparers</option>
            {preparers.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select data-testid="filter-form" className={sel} value={form} onChange={(e) => setForm(e.target.value)}>
            <option value="all">All forms</option>
            {forms.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <select data-testid="filter-window" className={sel} value={win} onChange={(e) => setWin(e.target.value)}>
            <option value="all">Any deadline</option>
            <option value="7">Due in 7 days</option>
            <option value="30">Due in 30 days</option>
            <option value="90">Due in 90 days</option>
            <option value="overdue">Overdue</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {COLS.map((c) => <Column key={c.key} col={c} items={filt(board[c.key])} />)}
      </div>
    </div>
  );
}
